<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import AiWorkflowHelp from '@/shared/components/AiWorkflowHelp.vue'
  import { Button } from '@/components/ui/button'
  import { Label } from '@/components/ui/label'
  import { Switch } from '@/components/ui/switch'
  import { type CustomerUser as User, AiSettingsService } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  import { genericErrorToast, genericSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'

  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user)

  // Translation setup
  const { t } = useTranslation({
    en: {
      title: 'Basic Settings',
      description: 'Activate or deactivate AI workflows and set AI base rules for new inquiries.',
      loading: 'Loading settings...',
      workflowsActive: 'Workflows Active',
      workflowsActiveDescription:
        'When deactivated, no AI workflows will be executed for new inquiries in your law firm',
      aiBaseRules: 'AI Base Rules',
      aiBaseRulesDescription:
        'These instructions are executed <strong>before a specific workflow is selected</strong> and therefore apply to all new inquiries.',
      afterMinimumData: 'After capturing minimum data',
      afterMinimumDataDescription:
        'These instructions are executed when the basic contact data is available (in simple cases: email address).',
      afterMinimumDataPlaceholder:
        'Describe how the AI should proceed after capturing minimum data...',
      afterInitialAssessment: 'After initial case assessment',
      afterInitialAssessmentDescription:
        'These instructions are executed when the AI has a basic understanding of the inquiry and can presumably determine the legal area and topic in the next step.',
      afterInitialAssessmentPlaceholder:
        'Describe how the AI should act after the initial assessment...',
      saveSettings: 'Save Settings',
      saving: 'Saving...',
      workflowsActivated: 'AI Workflows activated',
      workflowsDeactivated: 'AI Workflows deactivated',
      globalSettingsSaved: 'Global AI settings successfully saved',
      loadingError: 'Error loading AI settings',
      savingError: 'Error saving settings',
      globalSavingError: 'Error saving global settings',
      instructionsTooLong: 'Instructions may be a maximum of 5000 characters long'
    },
    de: {
      title: 'Grundeinstellungen',
      description:
        'Aktivieren oder deaktivieren Sie KI‑Workflows und legen Sie die KI‑Basisregeln für neue Anfragen fest.',
      loading: 'Lade Einstellungen...',
      workflowsActive: 'Workflows aktiv',
      workflowsActiveDescription:
        'Wenn deaktiviert, werden keine KI-Workflows für neue Anfragen in Ihrer Kanzlei ausgeführt',
      aiBaseRules: 'KI‑Basisregeln',
      aiBaseRulesDescription:
        'Diese Anweisungen werden ausgeführt, <strong>bevor ein konkreter Workflow ausgewählt ist</strong> und gelten damit für alle neuen Anfragen.',
      afterMinimumData: 'Nach Erfassung der Mindestangaben',
      afterMinimumDataDescription:
        'Diese Anweisungen werden aufgeführt, wenn die grundlegenden Kontaktdaten vorliegen (im einfachen Fall: E‑Mail‑Adresse).',
      afterMinimumDataPlaceholder:
        'Beschreiben Sie, wie die KI nach Erfassung der Mindestangaben vorgehen soll...',
      afterInitialAssessment: 'Nach erster Einordnung des Falls',
      afterInitialAssessmentDescription:
        'Diese Anweisungen werden aufgeführt, wenn die KI ein Basisverständnis der Anfrage hat und voraussichtlich im nächsten Schritt Rechtsgebiet und Thema bestimmen kann.',
      afterInitialAssessmentPlaceholder:
        'Beschreiben Sie, wie die KI nach der ersten Einordnung handeln soll...',
      saveSettings: 'Einstellungen speichern',
      saving: 'Speichern...',
      workflowsActivated: 'AI Workflows aktiviert',
      workflowsDeactivated: 'AI Workflows deaktiviert',
      globalSettingsSaved: 'Globale KI-Einstellungen erfolgreich gespeichert',
      loadingError: 'Fehler beim Laden der KI-Einstellungen',
      savingError: 'Fehler beim Speichern der Einstellung',
      globalSavingError: 'Fehler beim Speichern der globalen Einstellungen',
      instructionsTooLong: 'Anweisungen dürfen maximal 5000 Zeichen lang sein'
    }
  })

  // State
  const aiSettings = ref(null)
  const isLoading = ref(true)
  const isSavingSettings = ref(false)

  // Global AI settings form data
  const globalSettingsData = ref({
    on_case_pre_case_context_gathering_instructions: '',
    on_case_post_case_context_gathering_instructions: ''
  })

  // Global settings validation schema
  const globalSettingsValidationSchema = yup.object({
    on_case_pre_case_context_gathering_instructions: yup
      .string()
      .max(5000, t('instructionsTooLong')),
    on_case_post_case_context_gathering_instructions: yup
      .string()
      .max(5000, t('instructionsTooLong'))
  })

  const isGlobalSettingsValid = computed(() => {
    try {
      globalSettingsValidationSchema.validateSync(globalSettingsData.value)
      return true
    } catch {
      return false
    }
  })

  onMounted(async () => {
    const fetching = [fetchAiSettings()]
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
  })

  const fetchAiSettings = async () => {
    try {
      isLoading.value = true
      const response = await AiSettingsService.aiSettingsList({ limit: 1 })
      if (response.results && response.results.length > 0) {
        aiSettings.value = response.results[0]
        // Populate global settings data
        globalSettingsData.value = {
          on_case_pre_case_context_gathering_instructions:
            aiSettings.value.on_case_pre_case_context_gathering_instructions || '',
          on_case_post_case_context_gathering_instructions:
            aiSettings.value.on_case_post_case_context_gathering_instructions || ''
        }
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error)
      genericErrorToast(t('loadingError'))
    } finally {
      isLoading.value = false
    }
  }

  const handleWorkflowsToggle = async (enabled) => {
    if (!aiSettings.value || isSavingSettings.value) return

    try {
      isSavingSettings.value = true

      const response = await AiSettingsService.aiSettingsPartialUpdate({
        uuid: aiSettings.value.uuid,
        requestBody: {
          ai_workflows_enabled: enabled
        }
      })

      aiSettings.value = response
      genericSuccessToast(enabled ? t('workflowsActivated') : t('workflowsDeactivated'))
    } catch (error) {
      console.error('Error updating AI workflows setting:', error)
      genericErrorToast(t('savingError'))
      // Revert the toggle state on error
      if (aiSettings.value) {
        aiSettings.value.ai_workflows_enabled = !enabled
      }
    } finally {
      isSavingSettings.value = false
    }
  }

  const handleGlobalSettingsSave = async () => {
    if (!aiSettings.value || !isGlobalSettingsValid.value || isSavingSettings.value) return

    try {
      isSavingSettings.value = true

      const response = await AiSettingsService.aiSettingsPartialUpdate({
        uuid: aiSettings.value.uuid,
        requestBody: globalSettingsData.value
      })

      aiSettings.value = response
      genericSuccessToast(t('globalSettingsSaved'))
    } catch (error) {
      console.error('Error updating global AI settings:', error)
      genericErrorToast(t('globalSavingError'))
    } finally {
      isSavingSettings.value = false
    }
  }

  const goBackToWorkflows = () => {
    // Navigate back to the workflows page
    window.history.back()
  }
</script>

<template>
  <SettingsPage v-if="user">
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8"
    >
      <div class="text-gray-500">{{ t('loading') }}</div>
    </div>

    <div
      v-else
      class="space-y-8"
    >
      <!-- Page Header -->
      <div class="space-y-2">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('title') }}</h1>
        <p
          class="text-gray-600"
          v-html="t('description')"
        ></p>
      </div>

      <!-- AI Workflows Toggle Section -->
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-base font-medium text-gray-900">{{ t('workflowsActive') }}</h3>
            <p class="text-sm text-gray-600">
              {{ t('workflowsActiveDescription') }}
            </p>
          </div>
          <div v-if="aiSettings">
            <Switch
              :checked="aiSettings.ai_workflows_enabled"
              :disabled="isSavingSettings"
              class="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-gray-200"
              @update:checked="handleWorkflowsToggle"
            />
          </div>
        </div>
      </div>

      <!-- KI-Basisregeln (runs before any workflow selection) -->
      <div class="space-y-4">
        <div class="space-y-1">
          <h3 class="text-lg font-medium text-gray-900">{{ t('aiBaseRules') }}</h3>
          <p
            class="text-sm text-gray-600"
            v-html="t('aiBaseRulesDescription')"
          ></p>
        </div>

        <!-- Pre Case Instructions -->
        <div class="space-y-2">
          <Label for="global_pre_case_context_gathering_instructions">
            {{ t('afterMinimumData') }}
          </Label>
          <p class="text-sm text-gray-600">
            {{ t('afterMinimumDataDescription') }}
          </p>
          <RichTextEditor
            v-model="globalSettingsData.on_case_pre_case_context_gathering_instructions"
            :placeholder="t('afterMinimumDataPlaceholder')"
            :disabled="isSavingSettings"
            :enable-text-styling-buttons="false"
            :enable-code-view-button="false"
            :custom-toolbar-buttons="['bold', 'italic', 'underline']"
          />
          <AiWorkflowHelp />
        </div>

        <!-- Post Case Instructions -->
        <div class="space-y-2">
          <Label for="global_post_case_context_gathering_instructions">
            {{ t('afterInitialAssessment') }}
          </Label>
          <p class="text-sm text-gray-600">
            {{ t('afterInitialAssessmentDescription') }}
          </p>
          <RichTextEditor
            v-model="globalSettingsData.on_case_post_case_context_gathering_instructions"
            :placeholder="t('afterInitialAssessmentPlaceholder')"
            :disabled="isSavingSettings"
            :enable-text-styling-buttons="false"
            :enable-code-view-button="false"
            :custom-toolbar-buttons="['bold', 'italic', 'underline']"
          />
          <AiWorkflowHelp />
        </div>

        <div class="flex justify-end">
          <Button
            :disabled="!isGlobalSettingsValid || isSavingSettings"
            class="bg-purple-600 text-white hover:bg-purple-700"
            @click="handleGlobalSettingsSave"
          >
            {{ isSavingSettings ? t('saving') : t('saveSettings') }}
          </Button>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped></style>
